<script>
    import $num from '../../global/num';


    /**
     * Displays a number, text, or icon, floating around content aligned to one of the corners.
     * The information displayed is typically status related to the adjoining content. Place the
     * content you wish to float the badge around inside the default slot. To create a badge with
     * an icon, place the icon inside the `label` slot. If you want to create a badge that is not
     * floating with relation to some content (i.e. inline), please consider `ib-tag`.
     */
    export default {
        name: 'ib-badge',

        props: {
            /**
             * The current value displayed in the badge.
             */
            value: {
                type: [ Boolean, Number, String ],
                default: '!'
            },
            /**
             * Maximum value to display if value is a `Number`; may be `undefined`.
             */
            max: {
                type: Number,
                default: 100
            },
            /**
             * Position relative to content [top, bottom]; default is top.
             */
            position: {
                type: String,
                default: 'top'
            },
            /**
             * Alignment relative to content [start, end, left, right]; default is end.
             * This is advisory and alignment can change to accommodate screen space.
             */
            align: {
                type: String,
                default: 'end'
            },
            /**
             * Horizontal position adjustment in pixels, away from the content.
             */
            offsetx: {
                type: Number,
                default: 0
            },
            /**
             * Vertical position adjustment in pixels, away from the content.
             */
            offsety: {
                type: Number,
                default: 0
            },
            /**
             * If `true`, grow toward the content rather than away from it as the size of the
             * badge increases.
             */
            inward: Boolean,
            /**
             * Semantic color class.
             */
            tone: String,
            /**
             * Appearance modifier (`sm`, `dot`, `dot-sm`, `black`, `warning`, `arrow`, and `rectangle` are supported).
             */
            appearance: String,
            /**
             * If `true`, display a 1px border.
             */
            border: Boolean,
            /**
             * Wrapper tag.
             */
            tag: {
                type: String,
                default: 'span'
            }
        },

        data() {
            return {
                ivalue: null
            }
        },

        computed: {
            style() {
                const rtl = this.$rtl;
                const dot = this.appearance == 'dot';
                const position = this.position;
                const offsetx = this.offsetx;
                const offsety = this.offsety;
                const align = {
                    start: rtl ? 'right' : 'left',
                    end: rtl ? 'left' : 'right'
                }[this.align] || this.align;
                let r = {};
                r[position] = -offsety - (dot ? 5 : 8) + 'px';
                if (dot || this.inward) {
                    r[align] = -offsetx - (dot ? 5 : 6) + 'px';
                } else {
                    r[{ right: 'left', left: 'right' }[align]] = 'calc(100% - ' + (12 - offsetx) + 'px)';
                }
                return r;
            }
        },

        created() {
            if ($build.env != 'production') {
                const position = this.position;
                if (position != 'top' && position != 'bottom') {
                    throw new RangeError('Invalid position value');
                }
                const align = this.align;
                if (align != 'start' && align != 'end' && align != 'left' && align != 'right') {
                    throw new RangeError('Invalid align value');
                }
            }
        },

        watch: {
            value: {
                handler: 'update',
                immediate: true
            }
        },

        render(h) {
            const vm = this;
            const appearance = vm.appearance;
            const dot = appearance == 'dot' || appearance == 'dot-sm';
            return h(vm.tag, {
                'staticClass': '_bdg'
            }, [
                vm._t('default'),
                h('transition', {
                    'attrs': {
                        'name': '_tr-scale',
                        'appear': true
                    },
                    'on': {
                        'after-leave': vm.reset
                    }
                }, [
                    h('span', {
                        'staticClass': '_bdgw',
                        'class': {
                            '_bdgi': !vm.$slots.default,
                            '_bbrd': this.border,
                            ['bg-' + vm.tone]: !!vm.tone,
                            ['_e' + appearance]: !!appearance
                        },
                        'style': vm.style,
                        'attrs': {
                            'aria-atomic': true,
                            'aria-label': vm.$locale.data.Badge,
                            'aria-live': 'polite',
                            'role': 'status'
                        },
                        'directives': [{
                            'name': 'show',
                            'rawName': 'v-show',
                            'value': !!vm.value,
                            'expression': 'value'
                        }]
                    }, [
                        vm._t('label', [
                            vm._v(vm._s(dot ? null : vm.ivalue))
                        ])
                    ])
                ])
            ]);
        },

        methods: {
            reset() {
                this.ivalue = 0;
            },

            update() {
                const to = this.value;
                if (to) {  // unless empty string, null, undefined, or 0
                    this.ivalue = typeof to === 'number'
                        ? to < this.max
                            ? $num.int(to)
                            : $num.int(this.max) + '+'
                        : to;
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._bdg {
        display: inline-block;
        position: relative;
        /** TODO:REMOVE */
        > ._btn,
        > ._field {
            margin: 0;
            max-width: 100%;
        }
    }

    ._bdgw {
        position: absolute;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        padding: 3px;
        min-width: calc(~'1em + 6px');
        border-radius: 99px;
        line-height: 1;
        .font-size(11);
        font-weight: 600;
        background-color: @bg-alert;
        color: @bg-alert-fg;
        white-space: nowrap;
        .elevation(3, 0, .75);

        &._bbrd {
            border: 1px solid;
            background: #000;
            color: #fff;
        }
        &._esm {
            padding: 2px;
            min-width: 0;
            font-size: 10px;
        }
        &._edot {
            width: 12px;
            height: 12px;
            padding: 0;
            min-width: 0;
        }
        &._edot-sm {
            width: 8px;
            height: 8px;
            padding: 0;
            min-width: 0;
        }
        &._eblack {
            background-color: #000;
            color: #fff;
            border: 1px solid;
        }
        &._earrow {
            border: none;
            border-radius: 0;
            padding: 2px 8px;
            -webkit-padding-start: 4px;
            padding-inline-start: 4px;
            &::before {
                content: '';
                position: absolute;
                display: inline-block;
                top: 0;
                width: calc(~'.8839em + 2px');
                height: 70.7107%;  /** 1 / sqrt(2) */
                background-color: inherit;
                z-index: -1;
            }
        }
        &._ewarning {
            background: none;
            box-shadow: none;
            padding: 5px;
            .font-size(@fs6);
            z-index: 0;
            color: #000;
            &::before {
                content: '';
                position: absolute;
                top: 4px;
                left: calc(~'50% - .5em');
                width: 1em;
                height: 1em;
                transform: rotate(45deg);
                background-color: @bg-warning;
                border-radius: 2px;
                box-shadow: 2px 2px 4px rgba(0,0,0,0.2);
                z-index: -1;
            }
        }
        &._erectangle {
            border-radius: 0;
        }
    }

    [dir=ltr] ._bdgw,
    [dir=rtl] [dir=ltr] ._bdgw {
        &._earrow {
            transform-origin: 0 50%;
            &::before {
                left: 0;
                transform-origin: 0 0;
                transform: rotate(45deg);
            }
        }
    }

    [dir=rtl] ._bdgw,
    [dir=ltr] [dir=rtl] ._bdgw {
        &._earrow {
            transform-origin: 100% 50%;
            &::before {
                right: 0;
                transform-origin: 100% 0;
                transform: rotate(-45deg);
            }
        }
    }

    ._bdgi {
        position: static;
        pointer-events: auto;
    }
</style>

<ib-example id="basic" name="Basic">
    <template lang="pug">
        div
            ib-badge.outsetx-8(:value="value")
                ib-button(id="click", @click="value++") Click Me
            ib-button.outsetx-8(id="reset", @click="value = 0") Reset
    </template>
    <script>
        {
            data() {
                return {
                    value: 0
                };
            }
        }
    </script>
</ib-example>

<ib-example id="appearance" name="Appearance">
    <template lang="pug">
        div
            .inset-8
                ib-badge(id="dot", appearance="dot")
                    ib-tag appearance="dot"
            .inset-8
                ib-badge(id="dot", appearance="dot-sm")
                    ib-tag appearance="dot-sm"
            .inset-8
                ib-badge(id="warning", appearance="warning")
                    ib-tag appearance="warning"
            .inset-8
                ib-badge(id="arrow", appearance="arrow")
                    ib-tag appearance="arrow"
    </template>
</ib-example>

<ib-example id="icon" name="Icon">
    <template lang="pug">
        include ../../icon/basic
        include ../../icon/flag
        div
            ib-badge(border, appearance="sm")
                span.icon-64
                    +icon-flag-de
                template(v-slot:label)
                    +icon-basic-lock
            | &emsp;
            ib-badge(border, appearance="sm", tone="ok")
                span.icon-64
                    +icon-flag-it
                template(v-slot:label)
                    +icon-basic-checkmark
    </template>
</ib-example>
